import { faGripVertical } from '@fortawesome/pro-solid-svg-icons/faGripVertical';
import { Box } from '@osmosis/react-ui/components/Box';
import { Flex } from '@osmosis/react-ui/components/Box';
import { Icon } from '@osmosis/react-ui/components/Icon';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import { useSchemeContext } from '@osmosis/react-ui/context/SchemeContext';

import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQuizContext } from '../../../../context/QuizContext';
import { useDeviceType } from '@osmosis/nextjs-components/context/DeviceTypeProvider';
import {
  DragAndDropData,
  DragAndDropSubmittedAnswerType,
  QuizAnswer,
} from '../../../../types/quiz';
import { ItemsI } from '../../../dnd/types';
import { P } from '@osmosis/react-ui/components/Text';
import { QuizContainer } from '../../QuizContainer';
import { QuizDndList } from '../../QuizDndList';
import QuizSubmitButtons from '../../QuizSubmitButtons';
import { CorrectBanner, SkipQuizButton } from '../utils';
import { AnsweredItems } from './AnsweredItems';
import { QuizType } from '@osmosis/shared/types/quiz';

/**
 * Check if the user's answer is correct.
 */
export const checkIsCorrect = (
  correctAnswers: QuizAnswer[],
  currentAnswers: string[],
  subtype?: QuizType,
) => {
  const correctAnswerTexts = correctAnswers.map((answer) => answer.text);
  const selectedAnswerTexts = currentAnswers;

  if (subtype === QuizType.OrderedDragAndDrop) {
    // Order does matter
    return correctAnswerTexts.every(
      (correctText, index) => correctText === selectedAnswerTexts[index],
    );
  }
  if (subtype === QuizType.ExtendedDragAndDrop) {
    if (correctAnswerTexts.length !== selectedAnswerTexts.length) {
      return false;
    }
  }

  // Order doesn't matter
  return correctAnswerTexts.every((correctText) =>
    selectedAnswerTexts.includes(correctText),
  );
};

/**
 * Check if the items have an answer.
 * @param items The items to check.
 * @param currentData The current drag and drop data.
 * @returns True if the items have an answer, false otherwise.
 */
export const checkHasAnswer = (
  items: ItemsI | null,
  currentData: DragAndDropData,
) => {
  return items && !!items[currentData.answersText]?.length;
};

/**
 * DragAndDrop.
 */
const DragAndDrop = () => {
  const color = useColors();
  const { scheme } = useSchemeContext();
  const { currentItem, submitAnswer, isAnswered, currentResponseItem } =
    useQuizContext();
  const currentData = currentItem?.data as DragAndDropData;

  const [items, setItems] = useState<ItemsI | null>(null);
  const { isMobile } = useDeviceType();
  const correctAnswers = useMemo(
    () => currentData.answers.filter((answer) => answer.isCorrect),
    [currentData],
  );

  // Get answers user has currently selected
  const currentAnswers = (items?.[currentData.answersText] || []) as string[];

  // Get response answer data if user has already responded
  const responseAnswers = ((
    currentResponseItem?.response?.answer as DragAndDropSubmittedAnswerType
  )
    ?.map((item) => currentData.answers.find((a) => a.text === item))
    .filter(Boolean) || []) as QuizAnswer[];

  const initialItems = useMemo(
    () => ({
      [currentData.optionsText]: currentData.shuffledAnswers.map((a) => a.text),
      [currentData.answersText]: {
        values: [],
        params: {
          ordered: currentData.ordered,
        },
      },
    }),
    [currentData],
  );

  const isCorrect = checkIsCorrect(
    correctAnswers,
    currentAnswers,
    currentItem?.subtype,
  );

  return (
    <Flex flexDirection="column">
      <QuizContainer>
        <P mb={3} fontStyle="italic" color="gray40">
          {currentData.ordered ? (
            <FormattedMessage
              id="quiz.dnd.header.order.correct"
              defaultMessage="Drag the correct responses into the right column in the correct order."
            />
          ) : (
            <FormattedMessage
              id="quiz.dnd.header.order.any"
              defaultMessage="Drag the correct responses into the right column in the any order."
            />
          )}
        </P>

        <Box
          sx={{
            color: scheme === 'dark' ? color.white : color.dark,
          }}
          dangerouslySetInnerHTML={{ __html: currentData.question }}
          mb={4}
        />

        {!isAnswered && (
          <QuizDndList
            vertical={isMobile}
            getItems={(items) => {
              setItems(items);
            }}
            customRenders={{
              item: ({ ordered, value, index }) => {
                return (
                  <Flex
                    alignItems="center"
                    textAlign="left"
                    justifyContent="flex-start"
                    sx={{ justifyContent: 'space-between', width: '100%' }}
                  >
                    <Icon color={color.gray60} icon={faGripVertical} />
                    {ordered && (
                      <P
                        mr={3}
                        fontWeight="bold"
                        textAlign="left"
                        marginLeft="15px !important"
                      >
                        {index + 1}
                      </P>
                    )}

                    <P
                      mr={2}
                      ml="15px"
                      textAlign="left"
                      dangerouslySetInnerHTML={{ __html: `${value}` }}
                    />
                  </Flex>
                );
              },
            }}
            items={initialItems}
          />
        )}

        {isAnswered && (
          <AnsweredItems
            isCorrect={isCorrect}
            answers={responseAnswers}
            correctAnswers={correctAnswers}
          />
        )}

        {isAnswered && <CorrectBanner isCorrectAnswer={isCorrect} />}
        {checkHasAnswer(items, currentData) && !isAnswered && (
          <QuizSubmitButtons
            submit={(c) => submitAnswer(c, isCorrect, currentAnswers)}
          />
        )}
      </QuizContainer>
      <SkipQuizButton />
    </Flex>
  );
};

export default DragAndDrop;
